import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useScopedAuth } from "@/components/CurrentShopProvider";
import type { Tables } from "@/integrations/supabase/types";
import { repository } from "@/infrastructure/repositories";
import type { Condition } from "@/domain/repositoryInterface/Repository";
import { toScope } from "@/usecase/shared/authScope";
import { staffDeps } from "@/usecase/staff/deps";
import {
  createStaff as createStaffUsecase,
  updateStaff as updateStaffUsecase,
} from "@/usecase/staff/mutations";
import type {
  CreateStaffValues,
  UpdateStaffValues,
} from "@/usecase/staff/record";

export type StaffRow = Tables<"staff">;

type StaffStatus = NonNullable<StaffRow["status"]>;

const staffRepo = repository("staff");

export function useStaffList(statusFilter: string) {
  const { authUser } = useScopedAuth();

  return useQuery<StaffRow[]>({
    queryKey: ["staff", statusFilter, authUser?.shop_id],
    queryFn: async () => {
      const where: Condition[] = [{ column: "deleted_at", op: "isNull" }];

      if (authUser?.shop_id) {
        where.push({ column: "shop_id", op: "eq", value: authUser.shop_id });
      }
      // company_id filter removed (T-06): RLS scopes staff via shop → brand → company

      if (statusFilter && statusFilter !== "all") {
        where.push({ column: "status", op: "eq", value: statusFilter as StaffStatus });
      }

      const { data } = await staffRepo.findMany({
        where,
        orderBy: [{ column: "sort_order", ascending: true }],
      });
      return data;
    },
  });
}

export function useCreateStaff() {
  const queryClient = useQueryClient();
  const { authUser } = useScopedAuth();

  return useMutation({
    mutationFn: (values: CreateStaffValues) =>
      createStaffUsecase(values, toScope(authUser), staffDeps),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["staff"] }),
  });
}

export function useUpdateStaff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: UpdateStaffValues & { id: string }) =>
      updateStaffUsecase(input, staffDeps),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["staff"] }),
  });
}

/** Batch-update sort_order for reordering */
export function useReorderStaff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (items: { id: string; sort_order: number }[]) => {
      // Repository update is per-id, so we do parallel updates
      await Promise.all(
        items.map(({ id, sort_order }) =>
          staffRepo.update({ id, patch: { sort_order } }),
        ),
      );
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["staff"] }),
  });
}

export function useSoftDeleteStaff() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await staffRepo.update({ id, patch: { deleted_at: new Date().toISOString() } });
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["staff"] }),
  });
}
